'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Wallet,
  Users,
  Award,
  Vote,
  BarChart3,
  Shield,
  Coins,
  Globe,
  CheckCircle2,
  ArrowRight,
  Zap,
  Eye,
  Heart,
  TrendingUp
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const features = [
  {
    icon: Wallet,
    title: 'ウォレット／ID連携',
    description: 'Web3ウォレットと既存IDシステムをシームレスに連携。使いやすさとセキュリティを両立。'
  },
  {
    icon: Coins,
    title: 'トークン／バッジ',
    description: '貢献度に応じたトークン発行。実績を証明するNFTバッジで、参加意欲を向上。'
  },
  {
    icon: Vote,
    title: '提案・投票',
    description: '地域の意思決定に参加。透明性の高い投票システムで、公平な合意形成を実現。'
  },
  {
    icon: Heart,
    title: '参加インセンティブ',
    description: '活動に応じた報酬設計。地域通貨との連携で、実益のある参加体験を提供。'
  },
  {
    icon: BarChart3,
    title: 'KPIダッシュボード',
    description: '参加状況、貢献度、意思決定の履歴を可視化。データドリブンな運営を支援。'
  },
  {
    icon: Shield,
    title: 'ガバナンス設計',
    description: '地域特性に合わせた柔軟なガバナンスルール。段階的な権限設定で安全な運営。'
  }
]

const useCases = [
  {
    title: '地域まちづくりDAO',
    scenario: '住民・関係人口が共同でまちづくりに参加',
    features: [
      'まちづくり提案への投票権',
      'ボランティア活動でトークン獲得',
      '地域商店での割引特典'
    ],
    results: '関係人口の参加率300%向上'
  },
  {
    title: '観光振興DAO',
    scenario: '観光客がリピーターとして地域に継続的に関与',
    features: [
      '観光貢献度の可視化',
      'アンバサダープログラム',
      '限定体験への優先アクセス'
    ],
    results: 'リピート率45%達成'
  },
  {
    title: '地域産業支援DAO',
    scenario: '地域外の専門家が知見を提供し、産業振興に貢献',
    features: [
      'アドバイス提供で報酬獲得',
      'プロジェクト成果の共有',
      '事業投資への参加権'
    ],
    results: '新規事業創出15件/年'
  }
]

const benefits = [
  {
    icon: Users,
    title: '継続的な関係構築',
    description: '一過性の関係を、持続的な関わりに変える仕組み',
    metric: '平均参加期間 3倍'
  },
  {
    icon: Eye,
    title: '透明性の確保',
    description: 'ブロックチェーンによる改ざん不可能な記録',
    metric: '信頼度スコア 92%'
  },
  {
    icon: TrendingUp,
    title: '参加意欲の向上',
    description: '貢献の可視化とインセンティブで動機づけ',
    metric: 'アクティブ率 65%'
  }
]

export function DaoContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-800 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] opacity-20"></div>
        <div
          className="animate-fade-up container relative z-10"
        >
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">貢献の可視化とガバナンス</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              関係人口DAO
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              関係人口の動機づけと継続参加／透明な意思決定
            </p>
            <p className="text-lg mb-8 opacity-80">
              ウォレット／ID連携、トークン／バッジ、提案・投票、<br />
              参加インセンティブ、KPIダッシュボードで<br />
              地域と関係人口の新しい関係性を構築します。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary">
                デモを体験
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                ホワイトペーパー
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 100" className="w-full h-24 fill-background">
            <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {/* Web3の可能性 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <Globe className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">
              Web3で実現する新しい地域参加
            </h2>
            <p className="text-lg text-muted-foreground">
              ブロックチェーン技術により、地理的制約を超えて<br />
              誰もが地域づくりに参加できる仕組みを提供
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <CardTitle>{benefit.title}</CardTitle>
                      <Badge variant="secondary" className="mt-2">
                        {benefit.metric}
                      </Badge>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 主要機能 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">主要機能</h2>
            <p className="text-lg text-muted-foreground">
              関係人口の参加を促進する包括的な機能群
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <FadeUp delay={index * 0.05}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 活用事例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">活用事例</h2>
            <p className="text-lg text-muted-foreground">
              様々な地域課題に対応するDAO活用例
            </p>
          </FadeUp>

          <Tabs defaultValue="machidukuri" className="max-w-5xl mx-auto">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="machidukuri">まちづくり</TabsTrigger>
              <TabsTrigger value="tourism">観光振興</TabsTrigger>
              <TabsTrigger value="industry">産業支援</TabsTrigger>
            </TabsList>

            {useCases.map((useCase, index) => (
              <TabsContent 
                key={index} 
                value={index === 0 ? 'machidukuri' : index === 1 ? 'tourism' : 'industry'}
                className="mt-8"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl">{useCase.title}</CardTitle>
                    <CardDescription>{useCase.scenario}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold mb-3">実装機能</h4>
                        <ul className="space-y-2">
                          {useCase.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <CheckCircle2 className="h-5 w-5 text-primary" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-4 border-t">
                        <p className="text-lg font-semibold text-primary">{useCase.results}</p>
                      </div>
                      <Button className="w-full">
                        詳細な事例を見る
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* トークンエコノミー */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">トークンエコノミー設計</h2>
            <p className="text-lg text-muted-foreground">
              持続可能な地域経済圏を構築
            </p>
          </FadeUp>

          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">トークン獲得方法</h3>
                    <ul className="space-y-3">
                      {[
                        'イベント参加・ボランティア活動',
                        '地域課題解決への提案',
                        'SNSでの情報発信・拡散',
                        '地域産品の購入・推奨',
                        'スキル提供・メンタリング'
                      ].map((method, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Coins className="h-4 w-4 text-primary" />
                          <span className="text-sm">{method}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-4">トークン活用方法</h3>
                    <ul className="space-y-3">
                      {[
                        '地域施設・サービスの優待利用',
                        '限定イベントへの参加権',
                        '意思決定への投票権重み',
                        '地域通貨への交換',
                        'NFTバッジのアップグレード'
                      ].map((usage, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Award className="h-4 w-4 text-primary" />
                          <span className="text-sm">{usage}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-8 p-4 bg-primary/10 rounded-lg text-center">
                  <p className="text-sm font-semibold">
                    地域への貢献が、具体的な価値として還元される仕組み
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 導入ステップ */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">導入ステップ</h2>
            <p className="text-lg text-muted-foreground">
              段階的な導入で、安全かつ効果的にDAO運営を開始
            </p>
          </FadeUp>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: 'ビジョン策定',
                  description: 'DAOの目的、参加者、インセンティブ設計を明確化',
                  duration: '2週間'
                },
                {
                  step: '02',
                  title: 'システム構築',
                  description: 'スマートコントラクト開発、ウォレット連携、UI/UX設計',
                  duration: '1-2ヶ月'
                },
                {
                  step: '03',
                  title: 'パイロット運用',
                  description: '限定メンバーでの試験運用、フィードバック収集',
                  duration: '1ヶ月'
                },
                {
                  step: '04',
                  title: '本格展開',
                  description: '一般公開、コミュニティ形成、継続的な改善',
                  duration: '継続'
                }
              ].map((phase, index) => (
                <FadeUp delay={index * 0.1}
                  key={index}
                  className="flex gap-6"
                >
                  <div className="shrink-0">
                    <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                      {phase.step}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{phase.title}</h3>
                    <p className="text-muted-foreground mb-1">{phase.description}</p>
                    <Badge variant="secondary">{phase.duration}</Badge>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 料金プラン */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">料金プラン</h2>
            <p className="text-lg text-muted-foreground">
              規模と機能に応じた柔軟なプラン設計
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'パイロット',
                price: '月額 20万円〜',
                description: '小規模実証実験向け',
                features: [
                  '参加者数: 〜100名',
                  '基本的なトークン機能',
                  'シンプルな投票機能',
                  '月次レポート',
                  'コミュニティサポート'
                ]
              },
              {
                name: 'コミュニティ',
                price: '月額 50万円〜',
                description: '本格的なDAO運営向け',
                features: [
                  '参加者数: 〜1,000名',
                  '高度なトークンエコノミー',
                  'ガバナンス機能フル活用',
                  'API連携',
                  'データ分析ダッシュボード',
                  '優先サポート'
                ],
                popular: true
              },
              {
                name: 'エコシステム',
                price: 'お問い合わせ',
                description: '広域連携・大規模展開',
                features: [
                  '参加者数: 無制限',
                  'マルチトークン対応',
                  'カスタムガバナンス',
                  '他DAOとの連携',
                  '専用インフラ',
                  '戦略コンサルティング'
                ]
              }
            ].map((plan, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className={`h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                  {plan.popular && (
                    <div className="bg-primary text-white text-center py-2 text-sm font-semibold">
                      おすすめ
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="text-3xl font-bold mt-4">{plan.price}</div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6" variant={plan.popular ? 'default' : 'outline'}>
                      {plan.name === 'エコシステム' ? 'お問い合わせ' : '相談する'}
                    </Button>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <Zap className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              Web3で、地域の未来を共創する
            </h2>
            <p className="text-xl mb-8 opacity-90">
              関係人口DAOで、新しい地域参加の形を実現しませんか？
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary">
                デモを体験
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                ホワイトペーパーを読む
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}